// Diagnostic utility to help identify rendering issues

// Create a global diagnostic object
declare global {
  interface Window {
    __FLEXHELP_DIAGNOSTICS__: {
      renderAttempts: number;
      errors: Array<{
        message: string;
        stack?: string;
        timestamp: string;
        location?: string;
      }>;
      componentRenders: Record<string, number>;
      authState: {
        hasUser: boolean;
        isAuthenticated: boolean;
        isLoading: boolean;
        lastUpdated: string;
      } | null;
      log: (message: string, data?: any) => void;
      captureError: (error: Error, location?: string) => void;
      trackRender: (componentName: string) => void;
      trackAuthState: (state: { user: any; isAuthenticated: boolean; isLoading: boolean }) => void;
      getReport: () => string;
    };
  }
}

// Initialize the diagnostic object
export function initDiagnostics() {
  if (typeof window === 'undefined') return;

  // Only initialize once
  if (window.__FLEXHELP_DIAGNOSTICS__) return;

  window.__FLEXHELP_DIAGNOSTICS__ = {
    renderAttempts: 0,
    errors: [],
    componentRenders: {},
    authState: null,
    
    log: (message: string, data?: any) => {
      console.log(`[Diagnostics] ${message}`, data || '');
      
      // Also log to any visible debug elements
      const debugLog = document.getElementById('debug-log');
      if (debugLog) {
        const entry = document.createElement('div');
        entry.className = 'log-entry';
        entry.innerHTML = `
          <span class="log-time">${new Date().toLocaleTimeString()}</span>
          <span>${message}</span>
        `;
        debugLog.appendChild(entry);
        debugLog.scrollTop = debugLog.scrollHeight;
      }
    },
    
    captureError: (error: Error, location = 'unknown') => {
      const errorInfo = {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
        location
      };
      
      window.__FLEXHELP_DIAGNOSTICS__.errors.push(errorInfo);
      console.error(`[Diagnostics] Error in ${location}:`, error);
      
      // Also log to any visible error elements
      const errorDisplay = document.getElementById('error-display');
      if (errorDisplay) {
        errorDisplay.style.display = 'block';
        errorDisplay.textContent = `Error in ${location}: ${error.message}`;
      }
    },
    
    trackRender: (componentName: string) => {
      window.__FLEXHELP_DIAGNOSTICS__.renderAttempts++;
      
      if (!window.__FLEXHELP_DIAGNOSTICS__.componentRenders[componentName]) {
        window.__FLEXHELP_DIAGNOSTICS__.componentRenders[componentName] = 0;
      }
      
      window.__FLEXHELP_DIAGNOSTICS__.componentRenders[componentName]++;
      window.__FLEXHELP_DIAGNOSTICS__.log(`Component rendered: ${componentName}`);
    },
    
    trackAuthState: (state: { user: any; isAuthenticated: boolean; isLoading: boolean }) => {
      window.__FLEXHELP_DIAGNOSTICS__.authState = {
        hasUser: !!state.user,
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        lastUpdated: new Date().toISOString()
      };
      
      window.__FLEXHELP_DIAGNOSTICS__.log(
        `Auth state updated: ${JSON.stringify(window.__FLEXHELP_DIAGNOSTICS__.authState)}`
      );
    },
    
    getReport: () => {
      const { renderAttempts, errors, componentRenders, authState } = window.__FLEXHELP_DIAGNOSTICS__;
      
      return `
        === FlexHelp Diagnostics Report ===
        Generated: ${new Date().toISOString()}
        
        Render Attempts: ${renderAttempts}
        
        Auth State:
        ${authState ? JSON.stringify(authState, null, 2) : 'Not tracked yet'}
        
        Component Renders:
        ${Object.entries(componentRenders)
          .map(([component, count]) => `  ${component}: ${count}`)
          .join('\n')}
        
        Errors (${errors.length}):
        ${errors.map(err => 
          `  [${err.timestamp}] ${err.location}: ${err.message}`
        ).join('\n')}
      `;
    }
  };
  
  // Log initialization
  window.__FLEXHELP_DIAGNOSTICS__.log('Diagnostics initialized');
  
  // Add global error handlers
  const originalWindowOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (error) {
      window.__FLEXHELP_DIAGNOSTICS__.captureError(
        error, 
        `${source}:${lineno}:${colno}`
      );
    } else {
      window.__FLEXHELP_DIAGNOSTICS__.captureError(
        new Error(message as string),
        `${source}:${lineno}:${colno}`
      );
    }
    
    if (originalWindowOnError) {
      return originalWindowOnError.call(window, message, source, lineno, colno, error);
    }
    return false;
  };
  
  // Add unhandled rejection handler
  window.addEventListener('unhandledrejection', function(event) {
    window.__FLEXHELP_DIAGNOSTICS__.captureError(
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      'unhandled-promise-rejection'
    );
  });
  
  // Return the diagnostic object
  return window.__FLEXHELP_DIAGNOSTICS__;
}

// Export a function to track component renders
export function trackRender(componentName: string) {
  if (typeof window !== 'undefined' && window.__FLEXHELP_DIAGNOSTICS__) {
    window.__FLEXHELP_DIAGNOSTICS__.trackRender(componentName);
  }
}

// Export a function to track auth state
export function trackAuthState(state: { user: any; isAuthenticated: boolean; isLoading: boolean }) {
  if (typeof window !== 'undefined' && window.__FLEXHELP_DIAGNOSTICS__) {
    window.__FLEXHELP_DIAGNOSTICS__.trackAuthState(state);
  }
}

// Export a function to capture errors
export function captureError(error: Error, location?: string) {
  if (typeof window !== 'undefined' && window.__FLEXHELP_DIAGNOSTICS__) {
    window.__FLEXHELP_DIAGNOSTICS__.captureError(error, location);
  }
}

// Initialize diagnostics when this module is imported
initDiagnostics(); 